<template>
  <section>
    <div class="dialog-header">
      <h1>{{ title }}</h1>
    </div>
    <transition name="overlay-fade">
      <div class="dialog-content" v-show="sendOk">
        <p>
          Uw aanvraag wijziging klantgegevens is verzonden en wordt door ons
          verwerkt. Dit gebeurt doorgaans binnen 1 werkdag.
        </p>
        <div class="button-holder">
          <button class="pill-button" @click="$emit('close')">Doorgaan</button>
        </div>
      </div>
    </transition>
    <div class="dialog-content">
      <div class="toggle-section" v-if="type === 'new' || address.newCustomer">
        <!--  <p>Wat wilt u toevoegen?</p> -->
        <div class="toggle-buttons">
          <spec-button
            :selected="!useCustomerNumber"
            title="Bedrijfsgegevens"
            @action="switchToCompanyData"
          />
          <spec-button
            :selected="useCustomerNumber"
            title="Klantnummer"
            @action="switchToCustomerNumber"
          />
        </div>
      </div>

      <form v-if="useCustomerNumber" @submit.prevent="submitCustomerNumber">
        <!-- Klantnummer mode: alleen klantnummer input -->
        <div>
          <div class="row">
            <label>Klantnummer</label>
            <input
              class="grey-field"
              type="text"
              name="customerNumber"
              v-model="newCustomerNumber"
              placeholder="Voer uw klantnummer in"
              autofocus
              maxlength="7"
              :class="{
                invalid: !isValidCustomerNumber
              }"
            />
          </div>
        </div>

        <div class="button-holder">
          <button
            class="pill-button secondary"
            type="button"
            @click="$emit('close')"
          >
            Annuleren
          </button>
          <button
            class="pill-button"
            type="submit"
            :disabled="!isValidCustomerNumber"
          >
            Toevoegen
          </button>
        </div>
      </form>

      <form
        v-if="!useCustomerNumber"
        @submit.prevent="submitCustomer"
        @input="v$.newInvoiceAddress.$touch()"
      >
        <!-- Bedrijfsgegevens mode: volledig formulier -->
        <div>
          <div class="row">
            <label for="company">Bedrijfsnaam</label>
            <input
              id="company"
              class="grey-field"
              type="text"
              name="company"
              maxlength="50"
              v-model="newInvoiceAddress.company"
              :class="{
                invalid: v$.newInvoiceAddress.company.$invalid
              }"
              autofocus
            />
          </div>

          <div class="row">
            <label for="kvk">KVK-nummer</label>
            <input
              id="kvk"
              name="kvk"
              maxlength="8"
              class="grey-field"
              type="text"
              v-model="newInvoiceAddress.kvk"
              :class="{
                invalid: v$.newInvoiceAddress.kvk.$invalid
              }"
            />
          </div>

          <div class="row">
            <label for="contact">Contactpersoon</label>
            <input
              class="grey-field"
              type="text"
              id="contact"
              maxlength="30"
              v-model="newInvoiceAddress.name"
              :class="{
                invalid: v$.newInvoiceAddress.name.$invalid
              }"
            />
          </div>

          <div class="row">
            <label for="email">Factuur e-mail</label>
            <input
              class="grey-field"
              id="email"
              type="email"
              v-model="newInvoiceAddress.email"
              :class="{
                invalid: v$.newInvoiceAddress.email.$invalid
              }"
            />
          </div>

          <div class="row zip-row">
            <label for="zip">Postcode</label>
            <input
              id="zip"
              class="grey-field zip"
              type="text"
              v-model="newInvoiceAddress.zipCode"
              :class="{
                invalid: v$.newInvoiceAddress.zipCode.$invalid
              }"
            />

            <label for="nr">Huisnr.</label>
            <input
              id="nr"
              class="grey-field nr"
              type="text"
              v-model="newInvoiceAddress.number"
              @blur="getZipCode"
              :class="{
                invalid: v$.newInvoiceAddress.number.$invalid
              }"
            />

            <label for="add">Toev.</label>
            <input
              id="add"
              class="grey-field add"
              type="text"
              v-model="newInvoiceAddress.addition"
            />
          </div>

          <div class="row">
            <label for="street">Straat</label>
            <input
              id="street"
              class="grey-field"
              type="text"
              v-model="newInvoiceAddress.street"
              :class="{
                invalid: v$.newInvoiceAddress.street.$invalid
              }"
            />
          </div>

          <div class="row">
            <label for="city">Plaats</label>
            <input
              id="city"
              maxlength="30"
              class="grey-field"
              type="text"
              v-model="newInvoiceAddress.city"
              :class="{
                invalid: v$.newInvoiceAddress.city.$invalid
              }"
            />
          </div>
        </div>

        <div class="button-holder">
          <button
            class="pill-button secondary"
            type="button"
            @click="$emit('close')"
          >
            Annuleren
          </button>
          <button
            class="pill-button"
            type="submit"
            :disabled="
              v$.newInvoiceAddress.$invalid || !v$.newInvoiceAddress.$dirty
            "
          >
            Bewaren
          </button>
        </div>
      </form>
    </div>
  </section>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import {
  required,
  email,
  numeric,
  requiredIf,
  maxLength
} from '@vuelidate/validators'
import { sendMail } from '@/api/api'
import { zipCode } from '@/api/api'
import SpecButton from '@/components/ui/SpecButton.vue'

export default {
  components: {
    SpecButton
  },
  props: {
    callback: Function,
    type: {
      type: String,
      default: 'new'
    },
    address: {
      type: Object
    }
  },
  data() {
    return {
      v$: null,
      sendOk: false,
      useCustomerNumber: false,
      newCustomerNumber: null,
      //      newInvoiceAddress: this.address
      newInvoiceAddress: this.address
        ? { ...this.address }
        : {
            company: '',
            kvk: '',
            name: '',
            email: '',
            zipCode: '',
            number: '',
            addition: '',
            street: '',
            city: '',
            newCustomer: true
          }
    }
  },
  created() {
    this.v$ = useVuelidate.call(this)
  },
  computed: {
    isValidCustomerNumber() {
      const blockedNumbers = [
        '1000000',
        '1000001',
        '1019210',
        '1019216',
        '1019218',
        '1019220'
      ]
      return (
        this.newCustomerNumber &&
        /^\d{7}$/.test(this.newCustomerNumber) &&
        !blockedNumbers.includes(this.newCustomerNumber)
      )
    },
    title() {
      if (this.type === 'edit') {
        return 'Uw klantgegevens wijzigen'
      } else {
        return 'Factuurgegevens'
      }
    }
  },
  methods: {
    switchToCompanyData() {
      this.newInvoiceAddress = {
        company: '',
        kvk: '',
        name: '',
        email: '',
        zipCode: '',
        number: '',
        addition: '',
        street: '',
        city: '',
        newCustomer: true
      }
      this.useCustomerNumber = false
    },
    switchToCustomerNumber() {
      this.newInvoiceAddress = {
        company: '',
        kvk: '',
        name: '',
        email: '',
        zipCode: '',
        number: '',
        addition: '',
        street: '',
        city: '',
        newCustomer: true
      }
      this.useCustomerNumber = true
    },
    getZipCode() {
      zipCode(
        this.newInvoiceAddress.zipCode,
        this.newInvoiceAddress.number
      ).then(
        (address) => {
          this.newInvoiceAddress.street = address.street
          this.newInvoiceAddress.city = address.city
        },
        () => {
          this.newInvoiceAddress.street = ''
          this.newInvoiceAddress.city = ''
        }
      )
    },
    submitCustomerNumber() {
      this.newInvoiceAddress.projects = []
      this.newInvoiceAddress.references = []
      this.newInvoiceAddress.customerNumber = this.newCustomerNumber
      this.callback(this.newInvoiceAddress)
      this.$emit('close')
    },
    submitCustomer() {
      if (this.newInvoiceAddress.newCustomer) {
        this.newInvoiceAddress.projects = []
        this.newInvoiceAddress.references = []
        this.newInvoiceAddress.customerNumber = null
      } else {
        const contact = {
          title: 'Verzoek wijziging klantgegevens',
          subject: 'Verzoek wijziging klantgegevens',
          userInfo: {
            email: this.rootStore.user.username,
            name: this.rootStore.user.name || ''
          },
          message: `
        Klant:\n
        Bedrijfsnaam: ${this.newInvoiceAddress.company}
        Klantnummer: ${this.newInvoiceAddress.customerNumber}\n
        Klantgegevens:\n
        KVK : ${this.newInvoiceAddress.kvk}
        Adres : ${this.newInvoiceAddress.street}
        Postcode : ${this.newInvoiceAddress.zipCode}
        Plaats : ${this.newInvoiceAddress.city}
        `,
          to: 'servicedesk@rijnja.nl'
        }

        sendMail(contact).then((val) => {
          if (val === true) {
            this.sendOk = true
          } else {
            this.sendError = true
          }
        })
      }

      this.callback(this.newInvoiceAddress)
      this.$emit('close')
    }
  },
  validations: {
    newInvoiceAddress: {
      company: {
        required
      },
      email: {
        required: requiredIf(function () {
          return this.newInvoiceAddress.newCustomer
        }),
        email
      },
      name: {
        required: requiredIf(function () {
          return this.newInvoiceAddress.newCustomer
        })
      },
      street: {
        required
      },
      kvk: {
        numeric,
        required
      },
      city: {
        required
      },
      number: {
        numeric,
        required: requiredIf(function () {
          return this.newInvoiceAddress.newCustomer
        })
      },
      zipCode: {
        required,
        maxLength: maxLength(10)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
label {
  width: 105px;
  line-height: 34px;
}

.row {
  padding-bottom: 15px;
}
input {
  width: 285px;
}

.zip-row {
  display: flex;

  label:not(:first-child) {
    width: auto;
    margin-left: 7px;
  }

  .zip {
    width: 78px;
  }

  .nr {
    width: 59px;
  }

  .add {
    width: 40px;
  }
}

.toggle-section {
  margin-bottom: 20px;
}

.toggle-buttons {
  display: flex;
  gap: 10px;
}
</style>
